import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Key, Plus, Trash2, Save, TestTube2, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const empty = () => ({ name: "", provider_key: "cinetpay", api_key: "", secret_key: "", site_id: "", test_mode: true, active: true, currency: "XOF" });

// Fournisseurs pour lesquels le champ « Site ID / Marchand » n'est PAS requis.
const SITE_ID_OPTIONAL = new Set(["paytech", "fusion_money", "saspay", "wave", "flutterwave", "stripe", "paypal"]);
// Fournisseurs pour lesquels la clé secrète est obligatoire (webhook / API secret).
const SECRET_REQUIRED = new Set(["paytech", "stripe", "flutterwave"]);

export default function SuperAdminPaymentProviders() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(empty());
  const [testing, setTesting] = useState(null);

  const load = () => {
    api.get("/superadmin/payment-providers").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/superadmin/payment-providers/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const currentCatalog = catalog.find((c) => c.key === form.provider_key);

  const create = async () => {
    if (!form.api_key) { toast.error("Clé API requise"); return; }
    if (!SITE_ID_OPTIONAL.has(form.provider_key) && !form.site_id) { toast.error("Site ID / Marchand requis pour ce fournisseur"); return; }
    if (SECRET_REQUIRED.has(form.provider_key) && !form.secret_key) { toast.error("Clé secrète requise pour ce fournisseur"); return; }
    try { await api.post("/superadmin/payment-providers", form); toast.success("Fournisseur enregistré"); setOpenNew(false); setForm(empty()); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async (p) => {
    try {
      await api.patch(`/superadmin/payment-providers/${p.id}`, {
        name: p.name, provider_key: p.provider_key,
        api_key: p._new_api_key || "", secret_key: p._new_secret || "", site_id: p._new_site_id || "",
        test_mode: !!p.test_mode, active: !!p.active, currency: p.currency,
      });
      toast.success("Fournisseur mis à jour"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    try { await api.delete(`/superadmin/payment-providers/${p.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const test = async (p) => {
    setTesting(p.id);
    try {
      const { data } = await api.post(`/superadmin/payment-providers/${p.id}/test`);
      if (data.ok) toast.success(`✅ ${data.message || "Connexion API opérationnelle"}`);
      else toast.error(`❌ ${data.message || "Connexion API échouée"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(null); }
  };

  const update = (id, field, value) => setItems((prev) => prev.map((p) => p.id === id ? { ...p, [field]: value } : p));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">API Paiement</h1>
          <p className="text-sm text-slate-500">Fournisseurs de paiement configurés — clés chiffrées & masquées</p>
        </div>
        <Button className="btn-pill bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setOpenNew(true)} data-testid="sa-provider-new-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau fournisseur</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((p) => (
          <Card key={p.id} data-testid={`sa-provider-card-${p.id}`} className={p.active ? "" : "opacity-60"}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-orange-500 font-semibold flex items-center gap-1"><Key className="w-3 h-3" /> {p.provider_key.toUpperCase()}</div>
                  <div className="font-display text-xl font-bold mt-1">{p.name}</div>
                  <div className="text-xs text-slate-500 mt-1 font-mono">{p.id.slice(0, 8)}…</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.test_mode ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"}`}>{p.test_mode ? "TEST" : "LIVE"}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400"}`}>{p.active ? "ACTIF" : "INACTIF"}</span>
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Nom affiché</Label>
                  <Input value={p.name} onChange={(e) => update(p.id, "name", e.target.value)} data-testid={`sa-provider-name-${p.id}`} />
                </div>
                <div>
                  <Label className="text-xs">Clé API {p.has_api_key && <span className="text-slate-500 font-mono ml-2">{p.api_key_masked}</span>}</Label>
                  <Input type="password" placeholder={p.has_api_key ? "Laisser vide pour conserver" : "Coller la clé API…"} value={p._new_api_key || ""} onChange={(e) => update(p.id, "_new_api_key", e.target.value)} data-testid={`sa-provider-apikey-${p.id}`} />
                </div>
                {!SITE_ID_OPTIONAL.has(p.provider_key) && (
                  <div>
                    <Label className="text-xs">Site ID / Marchand {p.has_site_id && <span className="text-slate-500 font-mono ml-2">{p.site_id_masked}</span>}</Label>
                    <Input type="password" placeholder={p.has_site_id ? "Laisser vide pour conserver" : "Coller le site_id…"} value={p._new_site_id || ""} onChange={(e) => update(p.id, "_new_site_id", e.target.value)} data-testid={`sa-provider-siteid-${p.id}`} />
                  </div>
                )}
                <div>
                  <Label className="text-xs">Clé secrète (HMAC webhook) {p.has_secret_key && <span className="text-slate-500 font-mono ml-2">{p.secret_key_masked}</span>}</Label>
                  <Input type="password" placeholder={p.has_secret_key ? "Laisser vide pour conserver" : "Coller la clé secrète…"} value={p._new_secret || ""} onChange={(e) => update(p.id, "_new_secret", e.target.value)} data-testid={`sa-provider-secret-${p.id}`} />
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-800 pt-3">
                <label className="flex items-center gap-2 text-sm"><Switch checked={!p.test_mode} onCheckedChange={(v) => update(p.id, "test_mode", !v)} data-testid={`sa-provider-live-${p.id}`} /> Mode {p.test_mode ? "TEST" : "LIVE"}</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={!!p.active} onCheckedChange={(v) => update(p.id, "active", v)} data-testid={`sa-provider-active-${p.id}`} /> Actif</label>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Button size="sm" variant="outline" onClick={() => test(p)} disabled={testing === p.id} data-testid={`sa-provider-test-${p.id}`}>
                  {testing === p.id ? <span className="animate-pulse text-xs">…</span> : <><TestTube2 className="w-4 h-4 mr-1" /> Tester</>}
                </Button>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => save(p)} data-testid={`sa-provider-save-${p.id}`}><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`sa-provider-delete-${p.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="md:col-span-2"><CardContent className="p-10 text-center space-y-2" data-testid="sa-providers-empty">
            <ShieldAlert className="w-10 h-10 mx-auto text-slate-400" />
            <div className="font-semibold">Aucun fournisseur configuré</div>
            <div className="text-sm text-slate-500">Ajoutez CinetPay pour permettre aux entreprises de payer leur abonnement en ligne.</div>
          </CardContent></Card>
        )}
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="sa-provider-new-dialog">
          <DialogHeader><DialogTitle>Nouveau fournisseur de paiement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom affiché</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sa-provider-new-name" /></div>
            <div>
              <Label>Fournisseur</Label>
              <select className="w-full border border-slate-300 dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm" value={form.provider_key} onChange={(e) => setForm({ ...form, provider_key: e.target.value, name: catalog.find(c => c.key === e.target.value)?.label || "" })} data-testid="sa-provider-new-key">
                {catalog.length === 0 && <option value="cinetpay">CinetPay</option>}
                {catalog.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              {currentCatalog?.hint && <div className="text-xs text-slate-500 mt-1">{currentCatalog.hint}</div>}
            </div>
            <div><Label>Clé API <span className="text-red-500">*</span></Label><Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} data-testid="sa-provider-new-apikey" /></div>
            {!SITE_ID_OPTIONAL.has(form.provider_key) && (
              <div><Label>Site ID / Marchand <span className="text-red-500">*</span></Label><Input type="password" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} data-testid="sa-provider-new-siteid" /></div>
            )}
            <div><Label>Clé secrète (HMAC webhook) {SECRET_REQUIRED.has(form.provider_key) ? <span className="text-red-500">*</span> : <span className="text-slate-400 text-xs">(facultatif)</span>}</Label><Input type="password" value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })} data-testid="sa-provider-new-secret" /></div>
            <div><Label>Devise</Label>
              <select className="w-full border border-slate-300 dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="sa-provider-new-currency">
                <option value="XOF">XOF (FCFA)</option>
                <option value="XAF">XAF</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div className="flex items-center gap-4 pt-2">
              <label className="flex items-center gap-2 text-sm"><Switch checked={!form.test_mode} onCheckedChange={(v) => setForm({ ...form, test_mode: !v })} data-testid="sa-provider-new-live" /> Mode {form.test_mode ? "TEST" : "LIVE"}</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="sa-provider-new-active" /> Actif</label>
            </div>
            <div className="text-xs text-slate-500 flex gap-2 items-start pt-2"><ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" /> Les clés sont chiffrées avant enregistrement et jamais renvoyées en clair.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Annuler</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={create} data-testid="sa-provider-new-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
